import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Filters, Response, Pagination, ValidatorsProps } from '@ui/src';
import { CustomerProps } from '../../../customers/types';
import {
  corporateCustomersApi,
  CreateCorporateCustomerValidatorResponse,
} from '../services/corporate';
import { applicationsApi } from '../../../new-application/store/services';
import { NewApplicationListItem } from '../../../new-application/types';
import { customersComponentApi } from '../../../customers/store/services/customers';

import ROUTES from '../../../../routes';

export interface CorporateListState {
  isLoading: boolean;
  requestInProgress: boolean;
  corporateCustomers?: CustomerProps[];
  sort?: {
    sortProp: string;
    sortDir: string;
  };
  filters?: Filters;
  corporateCustomerValidations?: {
    createClientOfficialSchema: ValidatorsProps;
    updateClientOfficialSchema: ValidatorsProps;
  };
  hintVisible?: boolean;
  pagination?: Pagination;
  createdApplicationId?: string;
}

const initialState: CorporateListState = {
  isLoading: false,
  corporateCustomers: undefined,
  sort: undefined,
  requestInProgress: false,
  hintVisible: false,
  filters: undefined,
};

export const corporateCustomersListSlice = createSlice({
  name: 'corporateCustomersList',
  initialState,
  reducers: {
    setHintVisible: (state, action: PayloadAction<boolean>) => {
      state.hintVisible = action.payload;

      if (!action.payload) {
        state.createdApplicationId = undefined;
      }
    },
    clearCorporateList: (state) => {
      state.corporateCustomers = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        corporateCustomersApi.endpoints.getCreateCorporateCustomerValidator
          .matchFulfilled,
        (
          state,
          action: PayloadAction<CreateCorporateCustomerValidatorResponse>,
        ) => {
          state.corporateCustomerValidations = {
            createClientOfficialSchema:
              action.payload.data.createClientOfficialSchema,
            updateClientOfficialSchema:
              action.payload.data.updateClientOfficialSchema,
          };
        },
      )
      .addMatcher(
        corporateCustomersApi.endpoints.updateCorporateInfo.matchFulfilled,
        (state, action: PayloadAction<{ data: CustomerProps }>) => {
          const nextCorporateCustomers = state.corporateCustomers?.map(
            (item) => {
              const result = { ...item };

              if (result.id === action.payload.data.id) {
                result.companyName = action.payload.data.companyName;
              }

              return result;
            },
          );

          state.corporateCustomers = nextCorporateCustomers;
        },
      )
      .addMatcher(
        corporateCustomersApi.endpoints.getCorporateUsers.matchFulfilled,
        (state, action: PayloadAction<Response<CustomerProps>>) => {
          state.corporateCustomers = action.payload.data.edges.map((item) => {
            const result = { ...item.node };

            result.ids = item?.node?.id;

            result.url = generatePath(
              ROUTES.CUSTOMERS_CORPORATE_OVERVIEW_TRANSACTIONS,
              {
                customerId: result.id,
              },
            );

            return result;
          });

          state.pagination = action.payload.data.pageInfo;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.closeOrganization.matchFulfilled,
        (state, action: PayloadAction<{ data: CustomerProps }>) => {
          const nextIndividualCustomers = state.corporateCustomers?.map(
            (item) => {
              const result = { ...item };

              if (result.id === action.payload.data.id) {
                result.status = action.payload.data.status;
              }

              return result;
            },
          );

          state.corporateCustomers = nextIndividualCustomers;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.reopenOrganization.matchFulfilled,
        (state, action: PayloadAction<{ data: CustomerProps }>) => {
          const nextIndividualCustomers = state.corporateCustomers?.map(
            (item) => {
              const result = { ...item };

              if (result.id === action.payload.data.id) {
                result.status = action.payload.data.status;
              }

              return result;
            },
          );

          state.corporateCustomers = nextIndividualCustomers;
        },
      )
      .addMatcher(
        applicationsApi.endpoints.createCorporateApplication.matchFulfilled,
        (state, action: PayloadAction<{ data: NewApplicationListItem }>) => {
          state.createdApplicationId = action.payload.data.id;
        },
      );
  },
});

export const { setHintVisible, clearCorporateList } =
  corporateCustomersListSlice.actions;

export default corporateCustomersListSlice.reducer;
